/**
 * HighlightContainer.tsx — Per-highlight renderer
 *
 * The library calls this component once for each highlight.
 * We use the annotation_type to set colors, and MonitoredHighlightContainer
 * to show hover popups with note text.
 */
import React from "react";
import {
  useHighlightContainerContext,
  TextHighlight,
  MonitoredHighlightContainer,
} from "react-pdf-highlighter-extended";
import type { RemyxHighlight, AnnotationType } from "./types";
import { TYPE_COLORS } from "./types";

interface HighlightContainerProps {
  onDelete: (id: string) => void;
}

export const RemyxHighlightContainer: React.FC<HighlightContainerProps> = ({
  onDelete,
}) => {
  const { highlight, isScrolledTo } =
    useHighlightContainerContext<RemyxHighlight>();

  const annType = highlight.annotation_type || "highlight";
  const colors = TYPE_COLORS[annType] || TYPE_COLORS.highlight;

  const component = (
    <TextHighlight
      isScrolledTo={isScrolledTo}
      highlight={highlight}
      style={{
        background: colors.bg,
        mixBlendMode: "multiply",
        borderRadius: "2px",
        ...(isScrolledTo
          ? {
              outline: "2px solid rgba(255,255,255,0.7)",
              outlineOffset: "1px",
            }
          : {}),
      }}
    />
  );

  // Build tooltip content for hover
  const hasContent =
    highlight.note_text || annType !== "highlight";
  const highlightTip = hasContent
    ? {
        position: highlight.position,
        content: (
          <HighlightTooltip
            type={annType}
            noteText={highlight.note_text}
            selectedText={highlight.content?.text}
            authorName={
              highlight.author?.display_name || highlight.author?.name
            }
            onDelete={() => {
              onDelete(highlight.id);
            }}
            isOwn={!highlight.author}
          />
        ),
      }
    : undefined;

  return (
    <MonitoredHighlightContainer
      highlightTip={highlightTip}
      key={highlight.id}
      children={component}
    />
  );
};

/* ── Tooltip shown on hover ─────────────────────────────────── */

interface TooltipProps {
  type: AnnotationType;
  noteText?: string | null;
  selectedText?: string;
  authorName?: string;
  onDelete: () => void;
  isOwn: boolean;
}

const HighlightTooltip: React.FC<TooltipProps> = ({
  type,
  noteText,
  selectedText,
  authorName,
  onDelete,
  isOwn,
}) => {
  const colors = TYPE_COLORS[type] || TYPE_COLORS.highlight;

  return (
    <div
      style={{
        background: "#1e1e1e",
        border: "1px solid #333",
        borderRadius: "8px",
        padding: "10px 12px",
        maxWidth: "300px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        fontSize: "12px",
        color: "#e5e5e5",
        lineHeight: "1.5",
      }}
    >
      {/* Header row: type badge + author + delete */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: noteText ? "6px" : "0",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              padding: "1px 6px",
              borderRadius: "3px",
              background: colors.bg,
              color: colors.solid,
            }}
          >
            {type}
          </span>
          {authorName && (
            <span style={{ fontSize: "10px", color: "#888" }}>
              {authorName}
            </span>
          )}
        </div>
        {isOwn && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              background: "none",
              border: "none",
              color: "#666",
              cursor: "pointer",
              padding: "2px",
              lineHeight: 1,
              fontSize: "14px",
            }}
            onMouseOver={(e) =>
              ((e.target as HTMLElement).style.color = "#f87171")
            }
            onMouseOut={(e) =>
              ((e.target as HTMLElement).style.color = "#666")
            }
            title="Delete annotation"
          >
            ✕
          </button>
        )}
      </div>

      {/* Note content */}
      {noteText && (
        <p style={{ margin: 0, color: "#ccc" }}>{noteText}</p>
      )}
    </div>
  );
};
